import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import FormSkeleton from './FormSkeleton';
import ErrorState from './../../ErrorState/ErrorState';
import { useCachedFetch } from '../../../hooks/useCachedFetch';
import { AnimatedOnScroll } from '../../AnimatedOnScroll';
import { CONTACT_API } from '../../../config/api';
import { getRecaptchaToken } from '../../../utils/recaptcha';
import { useConsent } from '../../../hooks/useConsent';
import { triggerShowBanner } from '../../CookieBanner/CookieBanner';
import './Form.scss';

const ContactForm = () => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;

    const { data: mainData, loading, error, refetch } = useCachedFetch('/data/locales/mainData.json');

    const { hasConsentForForms } = useConsent();

    const handleRetry = useCallback(() => {
        refetch();
    }, [refetch]);

    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    const [emailError, setEmailError] = useState('');
    const [phoneFocused, setPhoneFocused] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [isFading, setIsFading] = useState(false);
    const [formKey, setFormKey] = useState(0);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const fileInputRef = useRef(null);

    const formData = useMemo(() => {
        return mainData?.TopMainData?.FormSection;
    }, [mainData]);

    useEffect(() => {
        if (loading) return;

        if (error) {
            setHasError(true);
            setIsLoading(false);
            return;
        }

        if (formData && formData[currentLang] && formData.en) {
            setHasError(false);
        } else {
            setHasError(true);
        }

        setIsLoading(false);
    }, [loading, error, formData, currentLang]);

    useEffect(() => {
        setEmailError("");
        setFormSubmitted(false);
        setIsFading(false);
        setFormKey(prev => prev + 1);
    }, [currentLang]);

    const currentTranslations = useMemo(() => {
        return formData?.[currentLang] || formData?.en || {};
    }, [formData, currentLang]);

    const errorMessages = useMemo(() => ({
        en: {
            nameRequired: "The field is required",
            nameInvalid: "Only letters and spaces are allowed",
            emailInvalid: "Invalid email address",
            emailDomain: "Email must contain a valid domain (e.g. gmail.com)",
            emailRequired: "The field is required",
            phoneInvalid: "Phone number must be between 10 and 12 digits",
            phoneRequired: "The field is required",
            keyboardLanguage: "Switch your keyboard",
            descriptionPlaceholder: "The more we know, the better.",
            submitting: "Sending...",
            messageTooLong: "Message must not exceed 2000 characters",
            fileTooLarge: "File must not exceed 5 MB",
            fileInvalidType: "Unsupported file type. Allowed: JPG, PNG, GIF, PDF, DOC, DOCX, TXT",
            subscribeError: "Submission error. Please try again.",
            rateLimitError: "Too many requests. Please try again later.",
            consentError: "Consent to personal data processing is required to submit the form",
        },
        ua: {
            nameRequired: "Поле обов'язкове для заповнення",
            nameInvalid: "Дозволені лише літери та пробіли",
            emailInvalid: "Невірна email адреса",
            emailDomain: "Email повинен містити дійсний домен (наприклад, gmail.com)",
            emailRequired: "Поле обов'язкове для заповнення",
            phoneInvalid: "Номер телефону повинен містити від 10 до 12 цифр",
            phoneRequired: "Поле обов'язкове для заповнення",
            keyboardLanguage: "Змініть мову на клавіатурі",
            descriptionPlaceholder: "Чим більше ми знаємо, тим краще.",
            submitting: "Відправка...",
            messageTooLong: "Повідомлення не повинно перевищувати 2000 символів",
            fileTooLarge: "Файл не повинен перевищувати 5 МБ",
            fileInvalidType: "Непідтримуваний тип файлу. Дозволені: JPG, PNG, GIF, PDF, DOC, DOCX, TXT",
            subscribeError: "Помилка відправки. Спробуйте ще раз.",
            rateLimitError: "Занадто багато запитів. Спробуйте пізніше.",
            consentError: "Для відправки форми необхідна згода на обробку персональних даних",
        },
        ru: {
            nameRequired: "Поле обязательно для заполнения",
            nameInvalid: "Разрешены только буквы и пробелы",
            emailInvalid: "Неверный адрес электронной почты",
            emailDomain: "Email должен содержать действительный домен (например, gmail.com)",
            emailRequired: "Поле обязательно для заполнения",
            phoneInvalid: "Номер телефона должен содержать от 10 до 12 цифр",
            phoneRequired: "Поле обязательно для заполнения",
            keyboardLanguage: "Смените язык на клавиатуре",
            descriptionPlaceholder: "Чем больше мы знаем, тем лучше.",
            submitting: "Отправка...",
            messageTooLong: "Сообщение не должно превышать 2000 символов",
            fileTooLarge: "Файл не должен превышать 5 МБ",
            fileInvalidType: "Неподдерживаемый тип файла. Разрешены: JPG, PNG, GIF, PDF, DOC, DOCX, TXT",
            subscribeError: "Ошибка отправки. Попробуйте еще раз.",
            rateLimitError: "Слишком много запросов. Попробуйте позже.",
            consentError: "Для отправки формы необходимо согласие на обработку персональных данных",
        },
        pl: {
            nameRequired: "Pole jest wymagane",
            nameInvalid: "Dozwolone są tylko litery i spacje",
            emailInvalid: "Nieprawidłowy adres email",
            emailDomain: "Email musi zawierać prawidłową domenę (np. gmail.com)",
            emailRequired: "Pole jest wymagane",
            phoneInvalid: "Numer telefonu musi zawierać od 10 do 12 cyfr",
            phoneRequired: "Pole jest wymagane",
            keyboardLanguage: "Zmień język klawiatury",
            descriptionPlaceholder: "Im więcej wiemy, tym lepiej.",
            submitting: "Wysyłanie...",
            messageTooLong: "Wiadomość nie może przekraczać 2000 znaków",
            fileTooLarge: "Plik nie może przekraczać 5 MB",
            fileInvalidType: "Nieobsługiwany typ pliku. Dozwolone: JPG, PNG, GIF, PDF, DOC, DOCX, TXT",
            subscribeError: "Błąd wysyłania. Spróbuj ponownie.",
            rateLimitError: "Zbyt wiele żądań. Spróbuj później.",
            consentError: "Do wysłania formularza wymagana jest zgoda na przetwarzanie danych osobowych",
        },
        de: {
            nameRequired: "Feld ist erforderlich",
            nameInvalid: "Nur Buchstaben i Leerzeichen sind erlaubt",
            emailInvalid: "Ungültige E-Mail-Adresse",
            emailDomain: "E-Mail muss eine gültige Domain enthalten (z.B. gmail.com)",
            emailRequired: "Feld ist erforderlich",
            phoneInvalid: "Telefonnummer muss zwischen 10 und 12 Ziffern enthalten",
            phoneRequired: "Feld ist erforderlich",
            keyboardLanguage: "Tastatursprache wechseln",
            descriptionPlaceholder: "Je mehr wir wissen, desto besser.",
            submitting: "Senden...",
            messageTooLong: "Nachricht darf 2000 Zeichen nicht überschreiten",
            fileTooLarge: "Datei darf 5 MB nicht überschreiten",
            fileInvalidType: "Nicht unterstützter Dateityp. Erlaubt: JPG, PNG, GIF, PDF, DOC, DOCX, TXT",
            subscribeError: "Fehler beim Senden. Bitte versuchen Sie es erneut.",
            rateLimitError: "Zu viele Anfragen. Bitte versuchen Sie es später erneut.",
            consentError: "Für das Absenden des Formulars ist die Zustimmung zur Verarbeitung personenbezogener Daten erforderlich",
        },
        es: {
            nameRequired: "El campo es obligatorio",
            nameInvalid: "Solo se permiten letras y espacios",
            emailInvalid: "Dirección de correo electrónico no válida",
            emailDomain: "El correo electrónico debe contener un dominio válido (por ejemplo, gmail.com)",
            emailRequired: "El campo es obligatorio",
            phoneInvalid: "El número de teléfono debe tener entre 10 y 12 dígitos",
            phoneRequired: "El campo es obligatorio",
            keyboardLanguage: "Cambiar idioma del teclado",
            descriptionPlaceholder: "Cuanto más sepamos, mejor.",
            submitting: "Enviando...",
            messageTooLong: "El mensaje no debe exceder los 2000 caracteres",
            fileTooLarge: "El archivo no debe exceder los 5 MB",
            fileInvalidType: "Tipo de archivo no soportado. Permitidos: JPG, PNG, GIF, PDF, DOC, DOCX, TXT",
            subscribeError: "Error al enviar. Por favor, inténtelo de nuevo.",
            rateLimitError: "Demasiadas solicitudes. Por favor, inténtelo más tarde.",
            consentError: "Para enviar el formulario se requiere el consentimiento para el procesamiento de datos personales",
        }
    }), []);

    const currentErrors = useMemo(() =>
        errorMessages[currentLang] || errorMessages.en,
        [currentLang, errorMessages]
    );

    const initialValues = useMemo(() => ({
        name: '',
        email: '',
        phone: '',
        description: '',
        file: null,
        company: '',
    }), []);

    const validationSchema = useMemo(() =>
        Yup.object({
            name: Yup.string()
                .matches(/^[A-Za-zА-Яа-яЁё\s]+$/, currentErrors.nameInvalid)
                .required(currentErrors.nameRequired),
            email: Yup.string()
                .email(currentErrors.emailInvalid)
                .matches(
                    /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
                    currentErrors.emailDomain
                )
                .required(currentErrors.emailRequired),
            phone: Yup.string()
                .matches(/^\+?\d{10,12}$/, currentErrors.phoneInvalid)
                .required(currentErrors.phoneRequired),
            description: Yup.string()
                .max(2000, currentErrors.messageTooLong)
                .nullable(),
            company: Yup.string().nullable(),
            file: Yup.mixed()
                .nullable()
                .test('fileSize', currentErrors.fileTooLarge, (value) => {
                    if (!value) return true;
                    return value.size <= 5 * 1024 * 1024;
                })
                .test('fileType', currentErrors.fileInvalidType, (value) => {
                    if (!value) return true;
                    const allowedTypes = [
                        'image/jpeg',
                        'image/jpg',
                        'image/png',
                        'image/gif',
                        'application/pdf',
                        'application/msword',
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        'text/plain'
                    ];
                    return allowedTypes.includes(value.type);
                }),
        }),
        [currentErrors]
    );

    const formatPhoneNumber = useCallback((value) => {
        const cleaned = value.replace(/\D+/g, '');
        if (cleaned.length > 0 && cleaned[0] !== '+') {
            return `+${cleaned}`;
        }
        return cleaned;
    }, []);

    const handleSubmit = useCallback(async (values, { resetForm }) => {
        if (!hasConsentForForms()) {
            setSubmitError(currentErrors.consentError);
            setFormSubmitted(false);
            setIsSubmitting(false);
            triggerShowBanner();
            setTimeout(() => {
                setSubmitError('');
            }, 10000);
            return;
        }

        setIsSubmitting(true);
        setSubmitError('');

        try {
            let recaptchaToken = '';
            try {
                recaptchaToken = await getRecaptchaToken('contact');
            } catch (err) {
                console.warn('reCAPTCHA error:', err);
            }

            const formData = new FormData();
            formData.append('name', values.name);
            formData.append('email', values.email);
            formData.append('phone', values.phone || '');
            formData.append('message', values.description || '');
            formData.append('company', values.company || '');
            formData.append('recaptchaToken', recaptchaToken);
            formData.append('language', currentLang);

            if (values.file) {
                formData.append('file', values.file);
            }

            const response = await fetch(CONTACT_API, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (response.ok) {

                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: 'form_lead_submitted',
                    form_name: 'contact_form'
                });

                setFormSubmitted(true);
                setSubmitError('');
                resetForm();

                setTimeout(() => {
                    setIsFading(true);
                    setTimeout(() => {
                        setFormSubmitted(false);
                        setIsFading(false);
                        setIsSubmitting(false);
                    }, 1000);
                }, 3000);
            } else if (response.status === 429) {
                setSubmitError(currentErrors.rateLimitError);
                setFormSubmitted(true);
                setIsSubmitting(false);
                setTimeout(() => {
                    setIsFading(true);
                    setTimeout(() => {
                        setFormSubmitted(false);
                        setIsFading(false);
                        setSubmitError('');
                    }, 1000);
                }, 3000);
            } else {
                const errorMessage = data.errors?.[0]?.msg || data.message || 'Ошибка отправки';
                setSubmitError(errorMessage);
                setFormSubmitted(true);
                setIsSubmitting(false);
                setTimeout(() => {
                    setIsFading(true);
                    setTimeout(() => {
                        setFormSubmitted(false);
                        setIsFading(false);
                        setSubmitError('');
                    }, 1000);
                }, 3000);
            }
        } catch (error) {
            console.error('Network error:', error);
            setSubmitError(currentErrors.subscribeError);
            setFormSubmitted(true);
            setIsSubmitting(false);
            setTimeout(() => {
                setIsFading(true);
                setTimeout(() => {
                    setFormSubmitted(false);
                    setIsFading(false);
                    setSubmitError('');
                }, 1000);
            }, 3000);
        }
    }, [currentErrors, hasConsentForForms, currentLang]);

    const handleNameInput = useCallback((e, setFieldTouched, setFieldError) => {
        if (e.target.value) {
            setFieldTouched('name', true, false);
            setFieldError('name', '');
        }
        e.target.value = e.target.value.replace(/[^A-Za-zА-Яа-яЁё\s]/g, '');
    }, []);

    const handleEmailInput = useCallback((e, setFieldTouched, setFieldError) => {
        const value = e.target.value;

        if (value && /[А-Яа-яЁё]/.test(value)) {
            setEmailError(currentErrors.keyboardLanguage);
        } else {
            setEmailError('');
        }

        e.target.value = value.replace(/[^A-Za-z0-9@._-]/g, '');

        if (value) {
            setFieldTouched('email', true, false);
            setFieldError('email', '');
        }
    }, [currentErrors]);

    const handlePhoneChange = useCallback((e, setFieldValue, setFieldTouched, setFieldError) => {
        const formattedPhone = formatPhoneNumber(e.target.value);
        if (formattedPhone.length <= 13) {
            setFieldValue('phone', formattedPhone);
        }

        if (formattedPhone) {
            setFieldTouched('phone', true, false);
            setFieldError('phone', '');
        }
    }, [formatPhoneNumber]);

    const handleFileChange = useCallback((event, setFieldValue) => {
        const file = event.currentTarget.files[0];
        setFieldValue('file', file);
    }, []);

    const handleRemoveFile = useCallback((setFieldValue, setFieldError) => {
        setFieldValue('file', null);
        setFieldError('file', '');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);

    const formatFileSize = useCallback((bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }, []);

    if (isLoading) {
        return <FormSkeleton />;
    }

    if (hasError || !formData) {
        return <ErrorState onRetry={handleRetry} theme={'dark'} />;
    }

    return (
        <Formik
            key={formKey}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
        >
            {({ setFieldValue, values, handleSubmit, setFieldTouched, setFieldError }) => (
                <Form className="contact__form" onSubmit={handleSubmit}>
                    <div className="contact__form__row">
                        <AnimatedOnScroll animation="fade-up" delay={0}>
                            <div className="contact__form__row__item">
                                <div className="contact__form__input__label">
                                    <Field
                                        name="name"
                                        type="text"
                                        id="name"
                                        placeholder=" "
                                        autoComplete="name"
                                        onInput={(e) => handleNameInput(e, setFieldTouched, setFieldError)}
                                    />
                                    <label htmlFor="name">{currentTranslations.name}</label>
                                    <ErrorMessage name="name" component="div" className="error__message" />
                                </div>

                                <div className="contact__form__input__label">
                                    <Field
                                        name="email"
                                        type="email"
                                        id="email"
                                        placeholder=" "
                                        autoComplete="email"
                                        onInput={(e) => handleEmailInput(e, setFieldTouched, setFieldError)}
                                    />
                                    <label htmlFor="email">{currentTranslations.email}</label>
                                    {emailError && !values.email && <div className="switch__keyboard__error">{emailError}</div>}
                                    <ErrorMessage name="email" component="div" className="error__message" />
                                </div>
                            </div>
                        </AnimatedOnScroll>
                        <AnimatedOnScroll animation="fade-up" delay={0}>
                            <div className="contact__form__row__item">
                                <div className="contact__form__input__label">
                                    <Field
                                        name="phone"
                                        type="text"
                                        id="phone"
                                        placeholder={phoneFocused || values.phone ? " " : "* Phone"}
                                        autoComplete="tel"
                                        value={values.phone}
                                        onFocus={() => {
                                            setPhoneFocused(true);
                                            if (!values.phone.startsWith('+')) {
                                                setFieldValue('phone', `+${values.phone}`);
                                            }
                                        }}
                                        onBlur={() => {
                                            setPhoneFocused(false);
                                        }}
                                        onChange={(e) => handlePhoneChange(e, setFieldValue, setFieldTouched, setFieldError)}
                                    />
                                    <label htmlFor="phone">{currentTranslations.phone}</label>
                                    <ErrorMessage name="phone" component="div" className="error__message" />
                                </div>

                                <div className="contact__form__input__label">
                                    <Field
                                        name="company"
                                        type="text"
                                        id="company"
                                        placeholder=" "
                                        autoComplete="organization"
                                        value={values.company || ''}
                                    />
                                    <label htmlFor="company">{currentTranslations.company}</label>
                                </div>
                            </div>
                        </AnimatedOnScroll>
                        <AnimatedOnScroll animation="fade-up" delay={0}>
                            <div className="contact__form__row__item">
                                <div className="contact__form__textarea__label">
                                    <Field
                                        name="description"
                                        as="textarea"
                                        id="description"
                                        className="contact__form__textarea"
                                        placeholder={currentErrors.descriptionPlaceholder}
                                        autoComplete="off"
                                    />
                                    <label htmlFor="description">{currentTranslations.textarea}</label>
                                    <ErrorMessage name="description" component="div" className="error__message" />
                                </div>
                            </div>
                        </AnimatedOnScroll>
                        <AnimatedOnScroll animation="fade-up" delay={0}>
                            <div className="contact__form__row__item">
                                <div className="contact__form__file__input">
                                    <input
                                        name="file"
                                        type="file"
                                        id="file"
                                        ref={fileInputRef}
                                        onChange={(event) => handleFileChange(event, setFieldValue)}
                                        style={{ display: 'none' }}
                                    />
                                    <label htmlFor="file" className="contact__form__file__label">
                                        {currentTranslations.attach}
                                    </label>

                                    {values.file && (
                                        <div className="file__info">
                                            <span className="file__name">
                                                📎 {values.file.name}
                                                <span className="file__size"> ({formatFileSize(values.file.size)})</span>
                                            </span>
                                            <button
                                                type="button"
                                                className="file__remove"
                                                onClick={() => handleRemoveFile(setFieldValue, setFieldError)}
                                                aria-label="Удалить файл"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    )}

                                    <ErrorMessage name="file" component="div" className="error__message" />
                                    {submitError && submitError === currentErrors.consentError && (
                                        <div className="error__message">
                                            <p>{submitError}</p>
                                        </div>
                                    )}
                                </div>

                                <div className="contact__form__buttons">
                                    {formSubmitted ? (
                                        <div
                                            className={`form__success__message ${isFading ? 'fade-out' : 'fade-in'}`}
                                        >
                                            <p>
                                                {submitError
                                                    ? submitError
                                                    : currentTranslations.success}
                                            </p>
                                        </div>
                                    ) : (
                                        <button
                                            type="submit"
                                            className="submit__button"
                                            disabled={isSubmitting}
                                        >
                                            <span></span>
                                            <span></span>
                                            <span></span>
                                            <span></span>
                                            {isSubmitting ? currentErrors.submitting : currentTranslations.submit}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </AnimatedOnScroll>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default React.memo(ContactForm);